import type { RequestState, VisitWindow } from '@servwell/shared';
import type { Tone } from './ui/Badge';

/**
 * The status vocabulary — design-language §6.2, as law: ONE plain-English vocabulary
 * per entity, defined once here and consumed everywhere (list badge, detail header,
 * board chip). Never a second vocabulary, never a raw DB value on screen.
 *
 * Values are the server's state-machine states (packages/shared/src/statemachines).
 */
type Display = { label: string; tone: Tone };

const fallback: Display = { label: '—', tone: 'neutral' };

export const JOB_STATUS: Record<string, Display> = {
  unscheduled: { label: 'Unscheduled', tone: 'warn' },
  scheduled: { label: 'Scheduled', tone: 'info' },
  in_progress: { label: 'In progress', tone: 'brand' },
  work_done: { label: 'Work done', tone: 'success' },
  invoiced: { label: 'Invoiced', tone: 'success' },
  closed: { label: 'Closed', tone: 'neutral' },
  canceled: { label: 'Canceled', tone: 'neutral' },
  on_hold: { label: 'On hold', tone: 'warn' },
};

export const VISIT_STATUS: Record<string, Display> = {
  scheduled: { label: 'Scheduled', tone: 'info' },
  en_route: { label: 'En route', tone: 'brand' },
  in_progress: { label: 'In progress', tone: 'brand' },
  completed: { label: 'Done', tone: 'success' },
  canceled: { label: 'Canceled', tone: 'neutral' },
};

export const QUOTE_STATUS: Record<string, Display> = {
  draft: { label: 'Draft', tone: 'neutral' },
  sent: { label: 'Sent', tone: 'info' },
  approved: { label: 'Approved', tone: 'success' },
  declined: { label: 'Declined', tone: 'danger' },
  expired: { label: 'Expired', tone: 'warn' },
  superseded: { label: 'Superseded', tone: 'neutral' },
};

export const INVOICE_STATUS: Record<string, Display> = {
  draft: { label: 'Draft', tone: 'neutral' },
  issued: { label: 'Unpaid', tone: 'info' },
  partially_paid: { label: 'Part paid', tone: 'warn' },
  paid: { label: 'Paid', tone: 'success' },
  void: { label: 'Void', tone: 'neutral' },
};

/** Repeating work. "Draft" never shows in practice — the server creates these active so
 *  they can book visits immediately — but the state exists, so it has a word. */
export const AGREEMENT_STATUS: Record<string, Display> = {
  draft: { label: 'Not started', tone: 'neutral' },
  active: { label: 'Running', tone: 'success' },
  paused: { label: 'Paused', tone: 'warn' },
  canceled: { label: 'Stopped', tone: 'neutral' },
  expired: { label: 'Finished', tone: 'neutral' },
};

/**
 * The shop's OWN vans and tools (107) — a different noun from the customer's equipment.
 *
 * `out_of_service` is `warn` rather than `danger`: a van in the shop is a plan somebody
 * already made, not something that has gone wrong, and spending the danger tone on it
 * leaves nothing louder for the reminder that actually lapsed. `retired` is `neutral`
 * for the same reason `canceled` is — it is over, not bad.
 *
 * Values are ASSET_STATES (packages/shared/src/schemas/asset.ts), and
 * `apps/web/test/fleet-assets.test.ts` fails if this map and that vocabulary stop covering
 * each other. (There is no `asset-ui.test.ts`; an earlier version of this comment named one,
 * which would send anyone verifying the claim to a file that does not exist.)
 */
export const ASSET_STATUS: Record<string, Display> = {
  active: { label: 'In service', tone: 'success' },
  out_of_service: { label: 'Out of service', tone: 'warn' },
  retired: { label: 'Retired', tone: 'neutral' },
};

/**
 * Requests, and the ONE vocabulary that names them.
 *
 * ⚠️ Keyed on `RequestState`, not `string` — deliberately unlike its neighbours above. As a
 * `Record<string, …>` this was a whitelist, and adding `archived` to the machine compiled
 * clean while the new status rendered on screen as the raw word "archived" in a grey pill.
 * Keyed properly, the next state added to the machine is a BUILD ERROR here.
 *
 * ⭐ It moved out of `RequestsPage` when the dispatch board's triage queue became a second
 * reader (114 part 5). Two screens naming the same five states is exactly the drift §6.2
 * forbids — and the closed-set typing came with it rather than being flattened to fit the
 * `Record<string, Display>` shape the others use.
 */
export const REQUEST_STATUS: Record<RequestState, Display> = {
  new: { label: 'Needs triage', tone: 'warn' },
  quoted: { label: 'Quoted', tone: 'info' },
  scheduled: { label: 'Scheduled', tone: 'success' },
  declined: { label: 'Declined', tone: 'neutral' },
  archived: { label: 'Archived', tone: 'neutral' },
  converted: { label: 'Converted to job', tone: 'success' },
};

/** The row's status is a string off the wire; this is where it re-enters the closed set. */
export function requestStatus(status: string): Display {
  return REQUEST_STATUS[status as RequestState] ?? { label: status, tone: 'neutral' };
}

/**
 * The arrival window the office promised — `visits.window`, one vocabulary (117).
 *
 * ⚠️ **Not a `Display`, and that is the point.** Every map above names a STATE and carries a
 * tone because it is drawn as a badge. A window is a promise about time: it has no tone,
 * nothing goes wrong when it is `all_day`, and spending a colour on it would put a second
 * status-looking pill beside the real one.
 *
 * 🔴 **`exact` is null, not a word.** For an exact booking the promise IS the scheduled
 * time, which every caller already prints; "Exact" beside "9:30 AM" is a vaguer second copy
 * of a fact on screen. Null means "say nothing here", and callers must handle it — which is
 * why the value type includes it rather than the map simply omitting the key.
 *
 * ⭐ Keyed on `VisitWindow`, following `REQUEST_STATUS`: a fifth window added to
 * `visits_window_check` becomes a BUILD ERROR here instead of rendering as the raw word
 * `evening` to somebody in a van.
 *
 * ⭐ It was private to `FleetRoutes.tsx` until the tech's next-stop card became a second
 * reader. Two screens naming the same four values is the drift §6.2 forbids — and the
 * private copy had no entry for `exact` at all, so it answered `undefined` for the DEFAULT
 * window, which is the one most visits carry.
 */
export const VISIT_WINDOW: Record<VisitWindow, string | null> = {
  exact: null,
  am: 'Morning',
  pm: 'Afternoon',
  all_day: 'All day',
};

/** The window is a string off the wire; this is where it re-enters the closed set. */
export function windowLabel(window: string): string | null {
  return VISIT_WINDOW[window as VisitWindow] ?? null;
}

export function display(map: Record<string, Display>, status: string): Display {
  return map[status] ?? fallback;
}
